import * as React from "react"
import { SafeImage } from "@/components/ui/safe-image"
import { accentColorFor } from "./types"
import { logoShape, type LogoShape } from "./logo-shape"

interface BrandLogoProps {
  name: string
  slug: string
  logoPath?: string | null
  /** Outer tile size in px. Default 52 (handoff list-card spec). */
  size?: number
  /** Override rounded corners. Default rounded-[10px] (handoff §6). */
  className?: string
  /**
   * FRIDGE-1495: a wide tile, so a wordmark gets room: 84 x 52, and 108 x 52
   * when the card's content box is 340 px or more (the parent must be an
   * `@container`). In the
   * 52 px square, 83 of the 127 logos on /brands painted under 14 px tall
   * (production 10/16/2026; KitchenAid 41 x 4). The initials fallback takes
   * the same box, so every card's text lines up.
   */
  wide?: boolean
  /**
   * FRIDGE-1495 (10/17/2026): with `wide`, the logo file's width over its
   * height (IndexBrand.logoAspect). A wordmark the tile would paint under
   * 14 px tall takes a 36 px row the card's full width instead (logo-shape.ts),
   * and the card wraps its text below it (BrandCardList). Absent: the tile.
   */
  aspect?: number | null
}

/**
 * The wide logo box per shape (logo-shape.ts). The row is 36 px tall with the
 * tile's 6 px padding, so a wordmark paints up to 22 px tall across the card,
 * about the height a text-like logo paints in the 108 px tile: KitchenAid
 * 220 x 22 in a 238 px card at 320 px. 'row-narrow' is the row under a 340 px
 * card and the 108 px tile from 340 px, where it paints 14 px or more.
 */
const WIDE_BOX: Record<LogoShape, string> = {
  tile: "h-[52px] w-[84px] @min-[340px]:w-[108px]",
  "row-narrow": "h-[36px] w-full @min-[340px]:h-[52px] @min-[340px]:w-[108px]",
  row: "h-[36px] w-full",
}

/**
 * Initials fallback derivation — matches the handoff `initials()` in
 * `brands-components.jsx`:
 *   - Strips parenthetical sub-brand suffix
 *   - Splits on whitespace/&/+/-/'
 *   - 1 word → first letter; ≥2 words → first letter of first two words
 */
function deriveInitials(name: string): string {
  const cleaned = (name || "").replace(/\(.*?\)/g, "").trim()
  const words = cleaned.split(/[\s&+\-']/).filter(Boolean)
  if (words.length === 0) return "?"
  if (words.length === 1) return words[0].charAt(0).toUpperCase()
  return (words[0].charAt(0) + (words[1] || "").charAt(0)).toUpperCase()
}

/**
 * BrandLogo — renders either:
 *   (a) the real brand logo from `logo_path` (most common when available), or
 *   (b) a tinted square tile with the brand's initial(s) in a deterministic
 *       accent color, per handoff §6.
 *
 * Border / radius matches the handoff: white bg + gray-200 border when there's
 * a real logo, transparent border + tinted accent bg for the fallback.
 */
export function BrandLogo({
  name,
  slug,
  logoPath,
  size = 52,
  className = "",
  wide = false,
  aspect = null,
}: BrandLogoProps) {
  const hasReal = !!logoPath && logoPath.length > 0
  const accent = accentColorFor(slug)
  const box = wide ? { className: WIDE_BOX.tile, style: undefined } : { className: "", style: { width: size, height: size } }

  if (hasReal) {
    if (wide) {
      const shape = logoShape(aspect)
      const row = shape !== "tile"
      return (
        <div
          data-logo-shape={row ? shape : undefined}
          className={`flex flex-shrink-0 items-center justify-center overflow-hidden rounded-[10px] border border-gray-200 bg-white p-[6px] ${WIDE_BOX[shape]} ${className}`}
        >
          <SafeImage
            src={logoPath!}
            alt={`${name} logo`}
            // The row's logo box runs up to the card's width by 22 px; 320 asks
            // next/image for 384 and 640 px files (the source when it is smaller).
            width={row ? 320 : 96}
            height={row ? 22 : 40}
            className="object-contain"
            // FRIDGE-1540: an inline size (the tile's inner box), so Chromium counts this lazy image as sized.
            style={{ width: "100%", height: "100%" }}
          />
        </div>
      )
    }
    return (
      <div
        className={`flex flex-shrink-0 items-center justify-center overflow-hidden rounded-[10px] border border-gray-200 bg-white ${className}`}
        style={{ width: size, height: size }}
      >
        <SafeImage
          src={logoPath!}
          alt={`${name} logo`}
          width={Math.round(size * 0.78)}
          height={Math.round(size * 0.78)}
          className="object-contain"
          // FRIDGE-1540: the logo's box is the tile's inner square (object-contain draws
          // the logo the same), so Chromium counts this lazy image as sized.
          style={{ width: Math.round(size * 0.78), height: Math.round(size * 0.78) }}
        />
      </div>
    )
  }

  return (
    <div
      className={`flex flex-shrink-0 items-center justify-center rounded-[10px] border border-transparent ${box.className} ${className}`}
      style={{
        ...box.style,
        background: `${accent}14`, // ~8% tint
      }}
      aria-hidden="true"
    >
      <span
        style={{
          color: accent,
          fontSize: Math.round((wide ? 52 : size) * 0.38),
          fontWeight: 900,
          letterSpacing: "-0.02em",
        }}
      >
        {deriveInitials(name)}
      </span>
    </div>
  )
}
